import { useQuery } from '@apollo/client';

import { useParams } from 'js/lib/useRouter';

import type { TopReview } from 'bundles/reviews-common/types/reviewsSharedTypes';
import type { ReviewsHeaderRatings } from 'bundles/reviews/components/ReviewsHeader';
import reviewsPageQuery from 'bundles/reviews/queries/ReviewsPage.graphql';
import type {
  ReviewsPageCdpMetadataFragment,
  ReviewsPageCourseFragment,
  ReviewsPageQuery,
  ReviewsPageQueryVariables,
  ReviewsPageSchemaReviewFragment,
} from 'bundles/reviews/queries/__generated__/ReviewsPage';
import { NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';

type ReviewsPageCourseConnection = ReviewsPageQuery['NaptimeQueries']['XdpV1Resource']['course'];
type ReviewsPageCourseElement = NonNullable<NonNullable<ReviewsPageCourseConnection>['elements'][number]>;

export type ReviewsPageCourseInfo = ReviewsPageCourseFragment & {
  xdpMetadata: ReviewsPageCdpMetadataFragment;
};

export type ReviewsPageCdpMetadata = ReviewsPageCdpMetadataFragment['cdpMetadata'];
export type ReviewsPageSchemaReview = ReviewsPageSchemaReviewFragment;

type ReviewsPageDataResult = {
  loading: boolean;
  error?: Error;
  notFound: boolean;
  courseInfo: ReviewsPageCourseInfo | null;
  description: string | null;
  ratings: ReviewsHeaderRatings | null;
  topReviews: TopReview[];
  schemaReviews: ReviewsPageSchemaReview[] | null;
};

const isDefined = <T>(value: T | null | undefined): value is T => value != null;

const isReviewsPageCourseInfo = (course: ReviewsPageCourseElement): course is ReviewsPageCourseInfo =>
  course.xdpMetadata.__typename === 'XdpV1_cdpMetadataMember';

export function useReviewsPageData(): ReviewsPageDataResult {
  const params = useParams();
  const slug = params?.slug;

  const { data, loading, error } = useQuery<ReviewsPageQuery, ReviewsPageQueryVariables>(reviewsPageQuery, {
    variables: { slug: slug ?? '' },
    skip: !slug,
    context: {
      ssrBlocking: true,
    },
  });

  const elements = (data?.NaptimeQueries?.XdpV1Resource?.course?.elements ?? []).filter(isDefined);
  const courseInfo = elements.find(isReviewsPageCourseInfo) ?? null;
  const cdpMetadata = courseInfo?.xdpMetadata.cdpMetadata ?? null;
  const description = cdpMetadata?.description ?? null;
  const ratings =
    cdpMetadata?.ratings?.averageFiveStarRating != null &&
    cdpMetadata.ratings.ratingCount != null &&
    cdpMetadata.ratings.commentCount != null
      ? {
          averageFiveStarRating: cdpMetadata.ratings.averageFiveStarRating,
          ratingCount: cdpMetadata.ratings.ratingCount,
          commentCount: cdpMetadata.ratings.commentCount,
        }
      : null;
  const topReviews =
    cdpMetadata?.topReviews?.filter(
      (review): review is TopReview =>
        review?.averageFiveStarRating != null &&
        review.timestamp != null &&
        review.authorName != null &&
        review.comment != null
    ) ?? [];
  const schemaReviews = courseInfo?.fetchProductReviews?.elements?.filter(isDefined) ?? null;

  const notFound = !loading && !!slug && elements.length === 0;

  if (notFound && inServerContext) {
    throw new NotFoundError();
  }

  return {
    loading: !!slug && loading,
    error: error ?? undefined,
    notFound,
    courseInfo,
    description,
    ratings,
    topReviews,
    schemaReviews,
  };
}
